/**
 * Reset password — user lands here from the recovery email. Supabase
 * sets a transient PASSWORD_RECOVERY session; we use updateUser({password})
 * to finalize and then redirect to /dashboard.
 *
 * MFA step-up: if the account has TOTP enabled, the recovery-link session is
 * only AAL1, and Supabase refuses to update the password until the session is
 * upgraded to AAL2 ("AAL2 session is required to update email or password when
 * MFA is enabled"). We detect the enrolled factor on mount, collect the 6-digit
 * code, and run challenge → verify to reach AAL2 before calling updateUser.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { friendlyError } from '@/lib/utils/friendly-errors';
import { PasswordInput } from '@/components/ui/PasswordInput';

const PWD_MIN = 10;

export function ResetPassword() {
  useEffect(() => { document.title = 'Choose a new password — Waypoint'; }, []);
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasRecoverySession, setHasRecoverySession] = useState<boolean | null>(null);

  // Set to the verified TOTP factor id when the account has MFA enabled — this
  // both gates the step-up call and drives the code input in the form.
  const [mfaFactorId, setMfaFactorId] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState('');

  // Verify we landed here from a real recovery flow, and detect whether MFA
  // step-up is required for this account. With PKCE the SDK is still
  // exchanging the recovery `?code=` for a session as this page mounts, so
  // poll briefly before declaring the link dead. Like a magic link, a recovery
  // link only completes in the browser that requested it.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      let hasSession = false;
      for (let attempt = 0; attempt < 30 && !hasSession; attempt++) {
        const { data: sessionData } = await supabase.auth.getSession();
        if (cancelled) return;
        hasSession = !!sessionData.session;
        if (!hasSession) await new Promise((r) => setTimeout(r, 200));
      }
      setHasRecoverySession(hasSession);
      if (!hasSession) return;

      // With MFA enrolled, the recovery session is AAL1 but can step up to AAL2.
      const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (cancelled || !aal) return;
      if (aal.nextLevel === 'aal2' && aal.currentLevel !== 'aal2') {
        const { data: factors } = await supabase.auth.mfa.listFactors();
        if (cancelled) return;
        const totp = factors?.totp?.find((f) => f.status === 'verified') ?? factors?.totp?.[0];
        if (totp) setMfaFactorId(totp.id);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  function validate(): string | null {
    if (password.length < PWD_MIN) return `Password must be at least ${PWD_MIN} characters.`;
    if (password !== confirm) return 'Passwords do not match.';
    if (!/[A-Z]/.test(password) || !/[a-z]/.test(password) || !/\d/.test(password)) {
      return 'Password must include upper-case, lower-case, and a number.';
    }
    if (mfaFactorId && mfaCode.trim().length < 6) {
      return 'Enter the 6-digit code from your authenticator app.';
    }
    return null;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const v = validate();
    if (v) { setError(v); return; }
    setSubmitting(true);
    try {
      // Step the recovery session up to AAL2 first when MFA is enrolled;
      // otherwise updateUser is rejected with "AAL2 session is required".
      if (mfaFactorId) {
        const { data: chall, error: cerr } = await supabase.auth.mfa.challenge({ factorId: mfaFactorId });
        if (cerr) throw cerr;
        const { error: verr } = await supabase.auth.mfa.verify({
          factorId: mfaFactorId,
          challengeId: chall.id,
          code: mfaCode.trim(),
        });
        if (verr) throw verr;
      }
      const { error: err } = await supabase.auth.updateUser({ password });
      if (err) throw err;
      setDone(true);
      // After 2s, kick to dashboard
      setTimeout(() => navigate('/dashboard', { replace: true }), 2000);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="mb-10 flex items-center justify-center gap-3">
          <span className="font-mono text-sm tracking-widest text-ink">WAYPOINT</span>
        </Link>
        <div className="rounded-xl border border-border bg-surface-1 p-8 shadow-glow">
          {done ? (
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-success-soft text-2xl">✓</div>
              <h1 className="mb-2 text-xl font-medium text-ink">Password updated</h1>
              <p className="text-sm text-ink-muted">Redirecting you to the dashboard…</p>
            </div>
          ) : hasRecoverySession === false ? (
            <div className="text-center">
              <h1 className="mb-2 text-xl font-medium text-ink">Link expired</h1>
              <p className="text-sm text-ink-muted">
                This reset link is no longer valid, or was opened in a different browser than the one that requested it. Please request a new one here.
              </p>
              <Link to="/forgot-password" className="mt-6 inline-block text-sm text-primary-bright hover:underline">
                Request new reset link
              </Link>
            </div>
          ) : (
            <>
              <h1 className="mb-1 text-xl font-medium text-ink">Set a new password</h1>
              <p className="mb-6 text-sm text-ink-muted">Choose a strong password you don't use anywhere else.</p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="password" className="mb-1.5 block text-xs font-medium text-ink-dim">
                    New password
                  </label>
                  <PasswordInput
                    id="password"
                    required
                    autoFocus
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    minLength={PWD_MIN}
                  />
                  <p className="mt-1 text-2xs text-ink-dim">{PWD_MIN}+ characters, upper + lower + number.</p>
                </div>
                <div>
                  <label htmlFor="confirm" className="mb-1.5 block text-xs font-medium text-ink-dim">
                    Confirm new password
                  </label>
                  <PasswordInput
                    id="confirm"
                    required
                    autoComplete="new-password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                  />
                </div>
                {mfaFactorId && (
                  <div>
                    <label htmlFor="mfaCode" className="mb-1.5 block text-xs font-medium text-ink-dim">
                      Authenticator code
                    </label>
                    <input
                      id="mfaCode"
                      type="text"
                      inputMode="numeric"
                      pattern="\d{6}"
                      maxLength={6}
                      autoComplete="one-time-code"
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                      placeholder="000000"
                      className="w-full rounded-md border border-border bg-surface-2 px-3 py-2 text-center text-lg font-mono tracking-[0.3em] text-ink outline-hidden focus:border-primary-line"
                    />
                    <p className="mt-1 text-2xs text-ink-dim">
                      Your account has two-factor authentication on. Enter the current 6-digit code from your authenticator app to confirm the change.
                    </p>
                  </div>
                )}
                {error && (
                  <div className="rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger">
                    {error}
                  </div>
                )}
                <button type="submit" disabled={submitting} className="btn btn-primary w-full py-2.5">
                  {submitting ? 'Updating…' : 'Update password'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
